import i18n from "i18n-react";
import independentLogo from "../../img/independent/logo.png";
import castLogo from "../../img/cast_mzate/logoCastLetrasNegras.png";
import {
    buildPdfKpiCard,
    buildPdfKeyValue,
    buildPdfSectionTitle,
    buildPdfTable,
    loadImageAsDataUrl
} from "../../components/dashboard/dashboardPdfUtils";
import { getDashboardPdfColors, resolveDashboardTheme } from "../../components/dashboard/dashboardTheme";
import {
    formatCurrency,
    formatNumber,
    formatPercent,
    formatPeriodDate
} from "./components/ordersDashboardFormatters";

const getStatusLabel = (status) => {
    const labels = {
        "0": i18n.translate("orders_dashboard_status_pending"),
        "1": i18n.translate("orders_dashboard_status_confirmed"),
        "2": i18n.translate("orders_dashboard_status_discarded")
    };
    return labels[status] || i18n.translate("default_all");
};

const getTypeLabel = (type) => {
    const labels = {
        "1": i18n.translate("default_profile"),
        "2": i18n.translate("default_group"),
        "3": i18n.translate("default_user")
    };
    return labels[type] || i18n.translate("default_all");
};

const findOptionLabel = (value, options = []) => {
    const option = options.find((item) => String(item.value) === String(value));
    return option?.label || value || "";
};

const getSelectedVisibilityValue = (filters, orgNodes = {}) => {
    if (filters.type === "1") return findOptionLabel(filters.ion, orgNodes.profiles);
    if (filters.type === "2") return findOptionLabel(filters.gon, orgNodes.groups);
    if (filters.type === "3") return findOptionLabel(filters.user, orgNodes.users);
    return "";
};

const TOP_PDF_LIMIT = 5;
const RATIO_BAR_WIDTH = 300;

const tableCell = (value, colors, options = {}) => ({
    text: value || "-",
    fontSize: 8,
    color: options.color || colors.text,
    alignment: options.alignment || "left"
});

const buildProductsRows = (products, colors) => (products || []).map((product, index) => ([
    tableCell(String(product.rank || index + 1), colors),
    tableCell(product.productCode, colors),
    tableCell(product.productDescription, colors),
    tableCell(formatNumber(product.quantity), colors, { alignment: "right" }),
    tableCell(formatCurrency(product.totalRevenue), colors, { alignment: "right" })
]));

const buildCustomersRows = (customers, colors) => (customers || []).map((customer, index) => ([
    tableCell(String(customer.rank || index + 1), colors),
    tableCell(customer.customerAlias || customer.customerName, colors),
    tableCell(formatNumber(customer.orderCount), colors, { alignment: "right" }),
    tableCell(formatCurrency(customer.totalSales), colors, { alignment: "right" })
]));

const buildSellersRows = (sellers, colors) => (sellers || []).map((seller, index) => ([
    tableCell(String(seller.rank || index + 1), colors),
    tableCell(seller.fullName || seller.username, colors),
    tableCell(formatNumber(seller.orderCount), colors, { alignment: "right" }),
    tableCell(formatCurrency(seller.totalSales), colors, { alignment: "right" }),
    tableCell(seller.belowAverage ? i18n.translate("orders_dashboard_below_average") : "-", colors, {
        color: seller.belowAverage ? colors.danger : colors.muted
    })
]));

const clampRatio = (value) => Math.min(100, Math.max(0, Number(value || 0)));

const buildVisitMetric = (label, value, colors) => ({
    stack: [
        { text: label, color: colors.muted, fontSize: 8, margin: [0, 0, 0, 2] },
        { text: formatNumber(value), color: colors.text, bold: true, fontSize: 11 }
    ],
    width: "33%"
});

const buildVisitOrderRatioReport = (dashboard, colors) => {
    const ratio = clampRatio(dashboard?.visitOrderRatio);
    const ratioWidth = Math.round((ratio * RATIO_BAR_WIDTH) / 100);

    return {
        table: {
            widths: ["*"],
            body: [[
                {
                    stack: [
                        {
                            columns: [
                                {
                                    stack: [
                                        { text: i18n.translate("orders_dashboard_visit_order_ratio"), color: colors.muted, fontSize: 8, margin: [0, 0, 0, 3] },
                                        { text: formatPercent(ratio), color: colors.text, bold: true, fontSize: 22 }
                                    ],
                                    width: 132
                                },
                                {
                                    stack: [
                                        {
                                            canvas: [
                                                { type: "rect", x: 0, y: 0, w: RATIO_BAR_WIDTH, h: 13, color: "#e9edf0" },
                                                { type: "rect", x: 0, y: 0, w: ratioWidth, h: 13, color: colors.muted }
                                            ],
                                            margin: [0, 11, 0, 5]
                                        },
                                        {
                                            columns: [
                                                { text: "0%", color: colors.muted, fontSize: 7, width: "50%" },
                                                { text: "100%", color: colors.muted, fontSize: 7, width: "50%", alignment: "right" }
                                            ],
                                            width: RATIO_BAR_WIDTH
                                        }
                                    ],
                                    width: "*"
                                }
                            ],
                            columnGap: 14
                        },
                        {
                            canvas: [{ type: "line", x1: 0, y1: 0, x2: 507, y2: 0, lineWidth: 0.5, lineColor: colors.border }],
                            margin: [0, 10, 0, 8]
                        },
                        {
                            columns: [
                                buildVisitMetric(i18n.translate("orders_dashboard_executed_visits"), dashboard?.executedVisits, colors),
                                buildVisitMetric(i18n.translate("orders_dashboard_productive_visits"), dashboard?.productiveVisits, colors),
                                buildVisitMetric(i18n.translate("orders_dashboard_visits_without_order"), dashboard?.visitsWithoutOrder, colors)
                            ],
                            columnGap: 10
                        }
                    ],
                    fillColor: colors.surfaceAlt || "#f7f9fa",
                    margin: [10, 10, 10, 10]
                }
            ]]
        },
        layout: {
            hLineColor: () => colors.border,
            vLineColor: () => colors.border,
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5
        },
        margin: [0, 4, 0, 0]
    };
};

const getLogoSrc = ({ instanceType, independent }) => {
    if (instanceType && independent && instanceType !== independent) {
        return castLogo;
    }
    return independentLogo;
};

export const buildOrdersDashboardPdfDefinition = async ({ dashboard, filters, orgNodes, rankings, instanceType, independent }) => {
    const pdfThemeColors = getDashboardPdfColors(resolveDashboardTheme({ instanceType, independent }));
    const generatedAt = new Date().toLocaleString("es-PY");
    const logoDataUrl = await loadImageAsDataUrl(getLogoSrc({ instanceType, independent }));
    const topCustomers = (rankings.customers || []).slice(0, TOP_PDF_LIMIT);
    const topSellers = (rankings.sellers || []).slice(0, TOP_PDF_LIMIT);
    const topProducts = (rankings.products || []).slice(0, TOP_PDF_LIMIT);
    const topRevenueProducts = (rankings.productsByRevenue || []).slice(0, TOP_PDF_LIMIT);
    const generatedAtLabel = String(i18n.translate("orders_dashboard_generated_at"));
    const headerDetails = [
        { text: `${generatedAtLabel}: ${generatedAt}`, fontSize: 8, color: pdfThemeColors.muted, margin: [0, 2, 0, 0] }
    ];

    return {
        pageSize: "A4",
        pageMargins: [32, 34, 32, 34],
        defaultStyle: {
            fontSize: 9,
            color: pdfThemeColors.text
        },
        footer: (currentPage, pageCount) => ({
            text: `${currentPage} / ${pageCount}`,
            alignment: "right",
            margin: [0, 0, 32, 0],
            fontSize: 8,
            color: pdfThemeColors.muted
        }),
        content: [
            {
                columns: [
                    {
                        stack: [
                            logoDataUrl
                                ? { image: logoDataUrl, width: 105 }
                                : { text: "" }
                        ],
                        width: 132
                    },
                    {
                        stack: [
                            { text: i18n.translate("orders_dashboard_sales_performance_report"), bold: true, fontSize: 17, color: pdfThemeColors.text },
                            ...headerDetails
                        ],
                        width: "*"
                    }
                ],
                columnGap: 18,
                margin: [0, 0, 0, 10]
            },
            {
                canvas: [{ type: "line", x1: 0, y1: 0, x2: 531, y2: 0, lineWidth: 1, lineColor: pdfThemeColors.border }],
                margin: [0, 0, 0, 8]
            },
            buildPdfSectionTitle(i18n.translate("orders_dashboard_active_filters"), pdfThemeColors),
            {
                columns: [
                    {
                        stack: [
                            buildPdfKeyValue(i18n.translate("orders_dashboard_status"), getStatusLabel(filters.status), pdfThemeColors),
                            buildPdfKeyValue(i18n.translate("default_type"), getTypeLabel(filters.type), pdfThemeColors),
                            buildPdfKeyValue(i18n.translate("orders_dashboard_filter_value"), getSelectedVisibilityValue(filters, orgNodes), pdfThemeColors)
                        ],
                        width: "50%"
                    },
                    {
                        stack: [
                            buildPdfKeyValue(i18n.translate("default_from"), formatPeriodDate(filters.dateStart), pdfThemeColors),
                            buildPdfKeyValue(i18n.translate("default_to"), formatPeriodDate(filters.dateEnd), pdfThemeColors)
                        ],
                        width: "50%"
                    }
                ]
            },
            buildPdfSectionTitle(i18n.translate("orders_dashboard_financial_summary"), pdfThemeColors),
            {
                columns: [
                    { stack: [buildPdfKpiCard(i18n.translate("orders_dashboard_total_sales"), formatCurrency(dashboard?.totalSales), pdfThemeColors)], width: "50%" },
                    { stack: [buildPdfKpiCard(i18n.translate("orders_dashboard_total_orders"), formatNumber(dashboard?.totalOrders), pdfThemeColors)], width: "50%" }
                ],
                columnGap: 8
            },
            {
                columns: [
                    { stack: [buildPdfKpiCard(i18n.translate("orders_dashboard_visit_order_ratio"), formatPercent(dashboard?.visitOrderRatio), pdfThemeColors)], width: "50%" },
                    { stack: [buildPdfKpiCard(i18n.translate("orders_dashboard_unique_customers"), formatNumber(dashboard?.uniqueCustomers), pdfThemeColors)], width: "50%" }
                ],
                columnGap: 8,
                margin: [0, 8, 0, 0]
            },
            {
                columns: [
                    { stack: [buildPdfKpiCard(i18n.translate("orders_dashboard_average_sale_per_seller"), formatCurrency(dashboard?.averageSalePerSeller), pdfThemeColors)], width: "50%" },
                    { stack: [buildPdfKpiCard(i18n.translate("orders_dashboard_average_ticket"), formatCurrency(dashboard?.averageTicket), pdfThemeColors)], width: "50%" }
                ],
                columnGap: 8,
                margin: [0, 8, 0, 0]
            },

            buildPdfSectionTitle(i18n.translate("orders_dashboard_detailed_performance"), pdfThemeColors),
            buildPdfSectionTitle(i18n.translate("orders_dashboard_highlighted_customers"), pdfThemeColors),
            buildPdfTable(["#", i18n.translate("default_customer"), i18n.translate("orders_dashboard_orders"), i18n.translate("orders_dashboard_total_sales")], buildCustomersRows(topCustomers, pdfThemeColors), [24, "*", 58, 82], pdfThemeColors),
            buildPdfSectionTitle(i18n.translate("orders_dashboard_sellers_ranking"), pdfThemeColors),
            buildPdfTable(["#", i18n.translate("default_user"), i18n.translate("orders_dashboard_orders"), i18n.translate("orders_dashboard_total_sales"), i18n.translate("orders_dashboard_status")], buildSellersRows(topSellers, pdfThemeColors), [24, "*", 58, 82, 72], pdfThemeColors),
            buildPdfSectionTitle(i18n.translate("orders_dashboard_top_products"), pdfThemeColors),
            buildPdfTable(["#", i18n.translate("default_code"), i18n.translate("default_description"), i18n.translate("default_quantity"), i18n.translate("orders_dashboard_total_sales")], buildProductsRows(topProducts, pdfThemeColors), [24, 62, "*", 58, 74], pdfThemeColors),
            {
                ...buildPdfSectionTitle(i18n.translate("orders_dashboard_top_revenue_products"), pdfThemeColors),
                pageBreak: 'before'
            },
            buildPdfTable(["#", i18n.translate("default_code"), i18n.translate("default_description"), i18n.translate("default_quantity"), i18n.translate("orders_dashboard_total_sales")], buildProductsRows(topRevenueProducts, pdfThemeColors), [24, 62, "*", 58, 74], pdfThemeColors),
            buildPdfSectionTitle(i18n.translate("orders_dashboard_visit_sale_conversion"), pdfThemeColors),
            buildVisitOrderRatioReport(dashboard, pdfThemeColors)
        ]
    };
};
